import { SeuComponentHandle, SeuComponentOutputHandle } from '@seu-types';

// Grid configuration for 2D canvas
export const SEU_CANVAS_GRID_CELL_SIZE = 288;
export const SEU_CANVAS_BUFFER_CELLS = 2;
// Also adjust width in canvas-component.scss if this value is changed (12.25rem)
export const SEU_CANVAS_COMPONENT_DRAG_PREVIEW_WIDTH = 196;

// First Operating condition initial position
export const SEU_OPERATING_CONDITION_X = 0;
export const SEU_OPERATING_CONDITION_Y = 0;

// Global parameters fixed position
export const SEU_GLOBAL_PARAMETERS_X = 0;
export const SEU_GLOBAL_PARAMETERS_Y = 1;

// Global ID for chimney
export const SEU_CHIMNEY_COMPONENT_ID = '-1';

// Component type for chimney
export const SEU_CHIMNEY_COMPONENT_TYPE = 'chimney';

// Component type for input stream
export const SEU_INPUT_STREAM_COMPONENT_TYPE = 'input-stream';

// Component types with special canvas styling (square, dark-blue card variant)
// Add new types here — the rest of the rendering logic picks this up automatically
export const SEU_MIXER_COMPONENT_TYPE = 'Mixer';
export const SEU_SPLITTER_COMPONENT_TYPE = 'Splitter';
export const SEU_SQUARE_VARIANT_COMPONENT_TYPES = new Set([
  SEU_MIXER_COMPONENT_TYPE,
  SEU_SPLITTER_COMPONENT_TYPE,
]);

export type SeuCoords = {
  x: number;
  y: number;
};

export type SeuCanvasComponentItem = {
  type: string;
  name: string;
  id: string;
  isWarning?: boolean;
  inputHandles: SeuComponentHandle[];
  outputHandles: SeuComponentOutputHandle[];
};

export type SeuCanvasListType = 'interconnection' | 'stack';

export type SeuCanvasViewMode = 'edit' | 'present' | 'results';

export type SeuCanvasMouseTool = 'pointer' | 'hand';

export type SeuCanvasResultsTool = 'layerview' | 'crosshairs';

export type SeuCanvasStreamStart = {
  connectionPoint: 'input' | 'output';
  index: number;
} & SeuCoords;

export type SeuCanvasRemoveComponentEvent = {
  componentId: string;
  type: SeuCanvasListType;
};

export type SeuCanvasInputOutputClickEvent = {
  event: MouseEvent;
  handleIndex?: number;
};

export type SeuCanvasInputStream = SeuCanvasComponentCoords & {
  inputStreamId: string;
};

export type SeuCanvasComponentCoords = {
  posX: number;
  posY: number;
};

export type SeuCanvas = {
  canvasStreams: SeuCanvasStream[];
  components: SeuCanvasComponent[];
  inputStreams: SeuCanvasInputStream[];
  chimney: SeuCanvasComponentCoords | null;
};

export type SeuCanvasStream = {
  canvasStreamId: string | null;
  name: string;
  linkedToChimney: boolean;
  inputStreamId: string | null;
  outputFromComponentId: string | null;
  outputFromComponentHandleId: string | null;
  inputForComponentId: string | null;
  inputForComponentHandleId: string | null;
};

export type SeuCanvasStreamPositionItem = SeuCanvasStream & {
  startX: number;
  startY: number;
  endX: number;
  endY: number;
  startSide: 'left' | 'right';
  endSide: 'left' | 'right';
};

export type SeuCanvasComponent = SeuCanvasComponentCoords & {
  componentId: string;
  inputOutputSwapped?: boolean; // If true, input is on right, output on left
};

export type SeuCanvasInputStreamApi = SeuCanvasComponentCoords & {
  inputStreamId: string;
};

export type SeuCanvasApi = {
  canvasStreams: SeuCanvasStreamApi[];
  components: SeuCanvasComponentApi[];
  inputStreams: SeuCanvasInputStreamApi[];
  chimney: SeuCanvasComponentCoords | null;
};

export type SeuCanvasStreamApi = {
  canvasStreamId: string | null;
  name: string;
  linkedToChimney: boolean;
  inputStreamId: string | null;
  outputFromComponentId: number | null;
  outputFromComponentHandleId: string | null;
  inputForComponentId: number | null;
  inputForComponentHandleId: string | null;
};

export type SeuCanvasComponentApi = SeuCanvasComponentCoords & {
  componentId: number;
  inputOutputSwapped?: boolean;
};
